<template>
  <div
    ref="target"
    :class="[!event.slug && 'opacity-50 filter grayscale pointer-events-none']"
    class="flex flex-col gap-3"
  >
    <BaseLocaleLink
      class="relative"
      :to="route"
      :class="!event.slug && 'pointer-events-none'"
    >
      <BaseRatio
        :ratio="1 / 2"
        size="cover"
        class="rounded-xl overflow-hidden hover:opacity-90 transition duration-150 ease-in-out bg-gray-200"
        :alt="event.name"
      >
        <div class="w-full h-full flex items-center justify-center">
          <BaseImage
            loading="lazy"
            reserved-height
            :src="logo || '/event-default-logo-1200x630.png'"
            class="w-full"
          />
        </div>
      </BaseRatio>
      <div
        v-if="event.is_has_ticket"
        class="absolute top-2 right-2 w-10 h-10 bg-white text-primary rounded-xl flex items-center justify-center"
      >
        <BaseIcon size="sm" name="outline_ticket" />
      </div>
    </BaseLocaleLink>
    <div class="flex flex-col gap-1 justify-between text-gray-900 relative">
      <div
        class="flex justify-between gap-2"
        :class="(!isOnlinePlatformOpen || !eventPlace) && 'pr-8'"
      >
        <BaseLocaleLink
          class="block text-lg font-bold hover:text-gray-600 leading-6 pt-0.5"
          :class="!event.slug && 'pointer-events-none'"
          :to="route"
        >
          {{ eventTitle }}
        </BaseLocaleLink>
        <BaseLink
          v-if="isOnlinePlatformOpen"
          :href="getHubLink(event.slug)"
          :target="isApp ? '_self' : '_blank'"
          class="!items-start"
        >
          <BaseButton size="none" class="h-8 px-2.5 bg-primary" rounded="full">
            <BaseIcon size="sm" name="outline_external_link" class="mr-1" />
            <div class="text-sm text-white">
              {{
                event.type
                  ? $t('events.online_platform')
                  : $t('events.meeting_system')
              }}
            </div>
          </BaseButton>
        </BaseLink>
      </div>
      <div v-if="eventCustomLanguage" class="flex gap-2">
        <BaseIcon
          name="outline_globe_alt"
          size="xs"
          title="Language"
          class="mt-[2px] flex-shrink-0"
        />
        <div class="text-sm">
          {{ eventCustomLanguage }}
        </div>
      </div>
      <div v-if="eventCustomPrice" class="flex gap-2">
        <BaseIcon
          name="outline_database"
          size="xs"
          title="Price"
          class="mt-[2px] flex-shrink-0"
        />
        <div class="text-sm">
          {{ eventCustomPrice }}
        </div>
      </div>
      <div
        v-if="eventCustomDate || event.type !== EventType.Course"
        class="flex gap-2"
      >
        <BaseIcon
          name="outline_calendar"
          size="xs"
          title="Date"
          class="mt-[2px] flex-shrink-0"
        />
        <div class="text-sm">
          {{
            eventCustomDate ||
            getHumanizedDatePeriod(
              event.date_from,
              event.date_to,
              event.timezone
            )
          }}
        </div>
      </div>
      <!-- <EventsCardOptionsMenu :event="event" :route="route" /> -->
      <div v-if="eventPlace" :class="isOnlinePlatformOpen && 'pr-8'">
        <component
          :is="eventPlaceLink ? BaseLink : BaseFragment"
          class="inline-flex"
          :href="eventPlaceLink"
          target="_blank"
        >
          <span class="inline-flex gap-2 items-center">
            <img
              v-if="event.country_id"
              class="h-[20px] w-auto border border-gray-300"
              :src="`https://purecatamphetamine.github.io/country-flag-icons/3x2/${event.country_id}.svg`"
            />
            <BaseIcon
              v-else
              name="outline_map_pin"
              size="xs"
              title="place"
              class="mt-[2px] flex-shrink-0"
            />
            <span class="text-sm">
              {{ eventPlace }}
            </span>
          </span>
        </component>
      </div>
      <div
        v-if="authStore.auth"
        class="absolute right-0"
        :class="isOnlinePlatformOpen ? 'bottom-0' : 'top-0'"
      >
        <FavoritesButton
          :entity-title="event.tagline || event.name"
          :entity-id="event.id"
          :initial-value="favoriteInitialValue ?? isFavorite"
          :entity-type="EntityType.Event"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { EventType, type EventLite } from '~/models/events'
import { useEvents } from '~/stores/events'
import { useAuth } from '~/stores/auth'
import { EntityType } from '~/models/common'

const BaseLink = resolveComponent('BaseLink')
const BaseFragment = resolveComponent('BaseFragment')
const { getHumanizedDatePeriod } = useDate()

const authStore = useAuth()
const { isApp } = useDetect()
const {
  getEventPlace,
  getEventPlaceLink,
  isHubOpen,
  getHubLink,
  getEventPageRoute,
} = useEvents()
const { getLocalMedia } = useLocalMedia()
const { locale } = useI18n()

interface PropsInterface {
  event: EventLite
  favoriteInitialValue?: boolean
}

const props = withDefaults(defineProps<PropsInterface>(), {
  favoriteInitialValue: undefined,
})

const route = getEventPageRoute(props.event.slug, props.event.type)

const eventTitle = computed<string>(() =>
  props.event?.tagline
    ? getFieldLocale(props.event.tagline, locale.value, true)
    : props.event?.name || ''
)

const eventCustomLanguage = computed<string>(() =>
  getFieldLocale(props.event?.custom_language || '', locale.value, true)
)

const eventCustomPrice = computed<string>(() =>
  getFieldLocale(props.event?.custom_price || '', locale.value, true)
)

const logo = computed(() => getLocalMedia(props.event.logo)?.url)

const eventPlace = computed(() => {
  return props.event ? getEventPlace(props.event) : ''
})

const eventPlaceLink = computed(() => {
  return props.event ? getEventPlaceLink(props.event) : ''
})

const eventCustomDate = computed<string>(() =>
  getFieldLocale(props.event?.custom_date || '', locale.value, true)
)

const isOnlinePlatformOpen = computed(
  () =>
    !!authStore.auth &&
    isHubOpen(props.event) &&
    (!!props.event.is_has_ticket || !!props.event.auto_reg)
)

// const downloadEventTicket = async () => {
//   try {
//     await downloadTicket(props.event.id)
//     pushNotification({
//       title: t('notifications.events.download_ticket_success'),
//     })
//   } catch (error) {
//     pushNotification({
//       title: t(`notifications.events.download_ticket_error`),
//       theme: 'destructive',
//     })
//     useLogError(error)
//   }
// }

const entity = {
  entity_id: props.event.id,
  entity_type: EntityType.Event,
}

const target = ref(null)
const { initCountersHelper, isFavorite } = useCountersHelper()

onMounted(async () => {
  await initCountersHelper({
    entity,
    target,
    isCheckFavorite: authStore.auth
      ? props.favoriteInitialValue === undefined
      : false,
  })
})
</script>
